/**
 * useSubstrateBatchWithMaterials Hook
 *
 * User Story: US-P6-002 - Recipe Cost Integration
 * Step 10: Integration with Substrate Batch Creation
 *
 * Provides integrated substrate batch creation that:
 * 1. Validates all materials are available (FIFO preview)
 * 2. Creates the substrate batch
 * 3. Records material usage (deducts from inventory batches)
 *
 * This hook combines:
 * - useSubstrateMutations for batch creation
 * - useMaterialAllocation for material deduction
 * - useRecipeCost for cost preview
 */

import { useState, useCallback } from 'react'
import { supabase } from '@/supabase-client'
import { toLocalDateString } from '../utils/dateUtils'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

/**
 * Call an edge function with authenticated session
 */
async function callFunction(functionName, action, data = {}) {
  // Get the user's session token for authenticated access
  const session = await supabase.auth.getSession()
  const accessToken = session?.data?.session?.access_token

  if (!accessToken) {
    throw new Error('Authentication required. Please log in.')
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'apikey': SUPABASE_ANON_KEY
    },
    body: JSON.stringify({ action, ...data }),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || result.message || 'Request failed')
  }

  return result
}

/**
 * Hook for creating substrate batches with integrated material allocation
 *
 * @returns {Object} - Hook state and methods
 */
export function useSubstrateBatchWithMaterials() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Get recipe with ingredients linked to inventory items
   * Uses default blocks_per_batch and 4.5kg block weight for basic info
   */
  const getRecipeWithIngredients = useCallback(async (recipeId) => {
    const result = await callFunction('recipes', 'get', {
      id: recipeId,
      include_cost: true  // Uses recipe's blocks_per_batch with default 4.5kg weight
    })
    return result.data
  }, [])

  /**
   * Get recipe cost calculated for specific batch parameters
   * Uses ratio-based formula: ingredient_kg = (parts / total_parts) × (blocks × block_weight_kg)
   */
  const getRecipeCostForBatch = useCallback(async (recipeId, totalBlocks, blockWeightKg) => {
    const result = await callFunction('recipes', 'cost', {
      id: recipeId,
      total_blocks: totalBlocks,
      block_weight_kg: blockWeightKg
    })
    return result.data
  }, [])

  /**
   * Preview material allocations for a recipe
   * Returns which batches would be consumed for each ingredient
   *
   * Uses ratio-based calculation:
   *   total_batch_kg = total_blocks × block_weight_kg
   *   total_parts = SUM(ingredient.parts)
   *   ingredient_kg = (ingredient.parts / total_parts) × total_batch_kg
   *
   * @param {string} recipeId - Recipe ID
   * @param {number} totalBlocks - Number of blocks to create
   * @param {number} blockWeightKg - Weight per block in kg (e.g., 4.5)
   * @returns {Promise<Object>} - Allocation preview with sufficiency check
   */
  const previewAllocations = useCallback(async (recipeId, totalBlocks, blockWeightKg = 4.5) => {
    try {
      setIsLoading(true)
      setError(null)

      // Get recipe with cost breakdown using ratio-based calculation
      const recipeCost = await getRecipeCostForBatch(recipeId, totalBlocks, blockWeightKg)

      if (!recipeCost) {
        throw new Error('Recipe not found')
      }

      // Get allocations for each inventory-tracked ingredient
      // The Edge Function already calculated the correct quantities using parts ratios
      const allocations = []
      let allAvailable = true

      for (const ingredient of recipeCost.ingredients || []) {
        if (!ingredient.track_inventory || !ingredient.item_id) {
          continue
        }

        // Use the calculated quantity_kg from the Edge Function (ratio-based)
        const quantityNeeded = ingredient.quantity_kg

        // Get FIFO allocation preview
        const allocationResult = await callFunction('inventory-batches', 'allocate', {
          item_id: ingredient.item_id,
          quantity: quantityNeeded
        })

        const allocation = allocationResult.data

        allocations.push({
          ingredient_id: ingredient.ingredient_id,
          item_id: ingredient.item_id,
          item_name: ingredient.item_name,
          parts: ingredient.parts,  // Include parts for reference
          quantity_needed: quantityNeeded,
          sufficient: allocation.sufficient,
          available_stock: allocation.total_available,
          shortfall: allocation.shortfall || 0,
          allocations: allocation.allocations || [],
          total_cost: allocation.total_cost || 0,
          blended_unit_cost: allocation.blended_unit_cost || 0,
          // For production card display (e.g., "3 × 15kg bags")
          purchase_unit: ingredient.purchase_unit || null,
          conversion_factor: ingredient.conversion_factor || null,
          unit_of_measure: ingredient.unit_of_measure || null
        })

        if (!allocation.sufficient) {
          allAvailable = false
        }
      }

      // Calculate total cost
      const totalCost = allocations.reduce((sum, a) => sum + (a.total_cost || 0), 0)

      return {
        recipe: recipeCost,
        allocations,
        all_available: allAvailable,
        total_cost: totalCost,
        cost_per_block: totalCost / totalBlocks,
        total_batch_kg: recipeCost.total_batch_kg,
        total_parts: recipeCost.total_parts
      }
    } catch (err) {
      console.error('Error previewing allocations:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [getRecipeCostForBatch])

  /**
   * Create substrate batch with material allocation
   *
   * This is the main function that:
   * 1. Pre-validates all materials are available
   * 2. Creates the substrate batch
   * 3. Records material usage for each ingredient
   *
   * Material deduction only happens AFTER batch creation succeeds.
   *
   * @param {Object} batchData - Substrate batch creation data
   * @param {string} batchData.recipe_id - Recipe ID
   * @param {string} batchData.block_format_id - Block format ID
   * @param {number} batchData.total_blocks - Number of blocks
   * @param {number} batchData.block_weight_kg - Weight per block (optional, fetched from format if not provided)
   * @param {string} batchData.mixed_at - Mixing date (optional)
   * @param {string} batchData.notes - Notes (optional)
   * @returns {Promise<Object>} - Created batch with material cost info
   */
  const createBatchWithMaterials = useCallback(async (batchData) => {
    try {
      setIsLoading(true)
      setError(null)

      const { recipe_id, block_format_id, total_blocks, block_weight_kg, mixed_at, notes, fills } = batchData

      // Get block format weight if not provided
      let blockWeightKg = block_weight_kg
      if (!blockWeightKg && block_format_id) {
        const { data: format } = await supabase
          .from('block_formats')
          .select('weight_kg')
          .eq('id', block_format_id)
          .single()
        blockWeightKg = format?.weight_kg || 4.5
      }
      blockWeightKg = blockWeightKg || 4.5

      // When no count is given (deferred-count flow), preview at recipe nominal
      // (recipe.blocks_per_batch) so the production card shows one mix's worth of
      // materials. Actual count is recorded later via count-bags.
      let previewBlocks = total_blocks
      if (!previewBlocks) {
        const { data: recipeRow } = await supabase
          .from('substrate_recipes')
          .select('blocks_per_batch')
          .eq('id', recipe_id)
          .single()
        previewBlocks = recipeRow?.blocks_per_batch || 54
      }

      // Step 1: Get recipe and preview allocations using ratio-based calculation
      const preview = await previewAllocations(recipe_id, previewBlocks, blockWeightKg)

      // Track warnings instead of blocking (silent pass on stock)
      const warnings = []
      if (!preview.all_available) {
        const insufficient = preview.allocations.filter(a => !a.sufficient)
        const items = insufficient.map(a =>
          `${a.item_name} (need ${a.quantity_needed.toFixed(2)}, have ${a.available_stock.toFixed(2)})`
        ).join(', ')
        warnings.push(`Note: Insufficient stock for ${items}. Batch created but materials not fully deducted.`)
      }

      // Step 2: Create the substrate batch (omit count/format fields when deferred)
      const createPayload = {
        recipe_id,
        mixed_at,
        notes
      }
      if (block_format_id) createPayload.block_format_id = block_format_id
      if (total_blocks) createPayload.total_blocks = total_blocks
      // Include multi-format fills if provided
      if (fills && fills.length > 0) {
        createPayload.fills = fills
      }
      const batchResult = await callFunction('substrate-batches', 'create', createPayload)

      if (!batchResult.success || !batchResult.data) {
        throw new Error('Failed to create substrate batch')
      }

      const substrateBatch = batchResult.data

      // Step 3: Record material usage for each ingredient
      // Flatten all allocations into a single array for the record-usage call
      const allAllocations = preview.allocations.flatMap(a =>
        (a.allocations || []).map(alloc => ({
          batch_id: alloc.batch_id,
          quantity: alloc.quantity,
          unit_cost: alloc.unit_cost
        }))
      )

      if (allAllocations.length > 0) {
        try {
          await callFunction('inventory-batches', 'record-usage', {
            allocations: allAllocations,
            substrate_batch_id: substrateBatch.id,
            usage_date: mixed_at || toLocalDateString(new Date())
          })
        } catch (usageError) {
          // Log the error but don't fail the batch creation
          // The batch was created successfully, just material tracking failed
          console.error('Warning: Material usage recording failed:', usageError)
          return {
            ...substrateBatch,
            material_cost: preview.total_cost,
            material_cost_per_block: preview.cost_per_block,
            material_warning: 'Batch created but material usage recording failed. Manual adjustment may be needed.'
          }
        }
      }

      return {
        ...substrateBatch,
        material_cost: preview.total_cost,
        // Cost per block is only meaningful once the count is known
        material_cost_per_block: total_blocks ? preview.cost_per_block : null,
        // Include any stock warnings (silent pass)
        ...(warnings.length > 0 && { material_warning: warnings.join(' ') })
      }
    } catch (err) {
      console.error('Error creating batch with materials:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [previewAllocations])

  /**
   * Get the last used recipe for a strain/species
   * Useful for auto-selecting a recipe based on recent usage
   *
   * @param {string} species - Species name (e.g., 'Oyster')
   * @returns {Promise<Object|null>} - Last used recipe or null
   */
  const getLastUsedRecipe = useCallback(async (species) => {
    try {
      // Query substrate_batches joined with recipes, filtered by compatible_species
      const { data, error: queryError } = await supabase
        .from('substrate_batches')
        .select(`
          recipe_id,
          created_at,
          substrate_recipes!inner (
            id,
            name,
            compatible_species,
            blocks_per_batch,
            is_current,
            active
          )
        `)
        .contains('substrate_recipes.compatible_species', [species])
        .eq('substrate_recipes.is_current', true)
        .eq('substrate_recipes.active', true)
        .order('created_at', { ascending: false })
        .limit(1)

      if (queryError) {
        console.error('Error fetching last used recipe:', queryError)
        return null
      }

      if (!data || data.length === 0) {
        return null
      }

      return data[0].substrate_recipes
    } catch (err) {
      console.error('Error getting last used recipe:', err)
      return null
    }
  }, [])

  return {
    // State
    isLoading,
    error,

    // Methods
    previewAllocations,
    createBatchWithMaterials,
    getLastUsedRecipe,

    // Utility
    clearError: useCallback(() => setError(null), [])
  }
}

export default useSubstrateBatchWithMaterials
